import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Paper,
  Typography,
  LinearProgress,
  useMediaQuery,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Description as ResumeIcon,
  TrendingUp as CareerIcon,
  Info as InfoIcon,
  RocketLaunch as GenerateIcon
} from '@mui/icons-material';

import { saveCareerReport } from '../../utils/careerReportService';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import { sanitizeError } from '../../utils/errorSanitizer';

// Import step components
import ResumeInput from '../Steps/ResumeInput';
import CareerPathSelection from '../Steps/CareerPathSelection'; // Now includes profile confirmation
import CareerContext from '../Steps/CareerContext';
import MarketVerdictCard from '../Steps/MarketVerdictCard';

const CareerPathWizard = ({ onComplete, user, existingReport, onRequireAuth, isAuthenticated }) => {
  const { settings: platformSettings } = usePlatformSettings();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(existingReport || {});
  const [stepValidation, setStepValidation] = useState({});
  const [autoSaving, setAutoSaving] = useState(false);
  const [currentReportId, setCurrentReportId] = useState(existingReport?.id || null);
  
  const autoSaveTimeoutRef = useRef(null);
  const isCreatingReportRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Step configuration (4 steps - combined career path + profile confirmation)
  const stepConfig = [
    {
      label: 'Resume Upload',
      icon: <ResumeIcon />,
      description: 'Upload or paste your resume'
    },
    {
      label: 'Career Path',
      icon: <CareerIcon />,
      description: 'Choose path & confirm profile'
    },
    {
      label: 'Additional Info',
      icon: <InfoIcon />,
      description: 'Optional context (recommended)'
    },
    {
      label: 'Market Verdict',
      icon: <GenerateIcon />,
      description: 'Your transition analysis'
    }
  ];

  // Step components mapping (4 steps - career path now includes profile confirmation)
  const steps = [
    ResumeInput,              // Step 0: Upload resume
    CareerPathSelection,      // Step 1: Choose path + confirm profile (combined)
    CareerContext,            // Step 2: Additional context
    MarketVerdictCard         // Step 3: Market verdict & generate
  ];

  // Calculate progress
  const calculateProgress = () => {
    const stepWeights = { 0: 30, 1: 35, 2: 15, 3: 10 };
    let totalWeight = 0;
    let completedWeight = 0;
    
    steps.forEach((step, index) => {
      const weight = stepWeights[index] || 10;
      totalWeight += weight;
      
      switch (index) {
        case 0: // Resume
          if (formData.resumeText && formData.resumeText.split(/\s+/).length >= 50) {
            completedWeight += weight;
          }
          break;
        case 1: // Career Path + Profile Confirmation (combined)
          if (formData.careerPath && formData.preprocessedData && formData.currentRole && formData.targetRole) {
            completedWeight += weight;
          } else if (formData.careerPath) {
            completedWeight += weight * 0.5; // Partial credit for just having path
          }
          break;
        case 2: // Additional Context (optional)
          completedWeight += weight * 0.5; // Partial credit since optional
          break;
        case 3: // Review
          // Don't count review step in progress
          break;
        default:
          break;
      }
    });
    
    return Math.min((completedWeight / totalWeight) * 100, 100);
  };

  const progress = useMemo(() => calculateProgress(), [formData]);

  // Auto-save functionality
  const autoSave = useCallback(async (data) => {
    if (!isAuthenticated || !user) {
      console.log('⏸️ Auto-save skipped: not authenticated');
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for debounced save
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setAutoSaving(true);
        
        // Debug log to track targetRole values
        console.log('💾 Auto-save triggered with targetRole:', data.targetRole);
        console.log('💾 Auto-save careerPath:', data.careerPath);
        console.log('💾 Auto-save selectedSuggestion:', data.selectedSuggestion?.title);
        
        // Create or update report
        let reportId = currentReportId;
        
        if (!reportId && !isCreatingReportRef.current) {
          isCreatingReportRef.current = true;
          try {
            reportId = await saveCareerReport(data, user.uid);
            setCurrentReportId(reportId);
            console.log('✅ Career report created:', reportId);
          } finally {
            isCreatingReportRef.current = false;
          }
        } else if (reportId) {
          await saveCareerReport({ ...data, id: reportId }, user.uid);
          console.log('✅ Career report updated:', reportId);
        }
        
        setAutoSaving(false);
      } catch (error) {
        console.error('❌ Auto-save error:', error);
        setAutoSaving(false);
      }
    }, 1000); // 1 second debounce
  }, [currentReportId, isAuthenticated, user]);

  // Update form data
  const updateFormData = useCallback((updates) => {
    setFormData(prev => {
      const newData = { ...prev, ...updates };
      autoSave(newData);
      return newData;
    });
  }, [autoSave]);

  // Handle step validation
  const handleStepValidation = useCallback((step, isValid) => {
    setStepValidation(prev => ({
      ...prev,
      [step]: isValid
    }));
  }, []);

  // Navigate between steps
  const handleNext = useCallback(async () => {
    if (currentStep === steps.length - 1) {
      // On final step, check authentication then trigger completion
      if (!isAuthenticated && onRequireAuth) {
        console.log('🔒 Authentication required to generate plan');
        onRequireAuth();
        return;
      }
      
      // Force save before generating plan
      console.log('💾 Forcing final save before generating plan...');
      try {
        setAutoSaving(true);
        
        let reportId = currentReportId;
        
        // Create report if it doesn't exist
        if (!reportId && !isCreatingReportRef.current) {
          isCreatingReportRef.current = true;
          try {
            reportId = await saveCareerReport(formData, user.uid);
            setCurrentReportId(reportId);
            console.log('✅ Career report created (final save):', reportId);
          } finally {
            isCreatingReportRef.current = false;
          }
        } else if (reportId) {
          // Update existing report
          await saveCareerReport({ ...formData, id: reportId }, user.uid);
          console.log('✅ Career report updated (final save):', reportId);
        }
        
        setAutoSaving(false);
        
        // Now proceed to generate plan
        if (onComplete && reportId) {
          console.log('✅ Career wizard complete, generating plan:', reportId);
          onComplete({
            ...formData,
            careerReportId: reportId,
            reportType: 'career'
          });
        } else {
          console.error('❌ No career report ID after save');
          alert('Error: Report could not be saved. Please try again.');
        }
      } catch (error) {
        console.error('❌ Error saving career report:', error);
        setAutoSaving(false);
        alert('Error saving report: ' + sanitizeError(error));
      }
    } else {
      // Moving to next step
      setCurrentStep(prev => prev + 1);
      
      // Force save when moving to review step (step before last)
      if (currentStep === steps.length - 2 && isAuthenticated && user) {
        console.log('💾 Saving before review step...');
        try {
          let reportId = currentReportId;
          if (!reportId && !isCreatingReportRef.current) {
            isCreatingReportRef.current = true;
            try {
              reportId = await saveCareerReport(formData, user.uid);
              setCurrentReportId(reportId);
              console.log('✅ Career report created (pre-review):', reportId);
            } finally {
              isCreatingReportRef.current = false;
            }
          } else if (reportId) {
            await saveCareerReport({ ...formData, id: reportId }, user.uid);
            console.log('✅ Career report updated (pre-review):', reportId);
          }
        } catch (error) {
          console.error('❌ Error in pre-review save:', error);
        }
      }
    }
  }, [currentStep, steps.length, onComplete, formData, currentReportId, isAuthenticated, onRequireAuth, user]);

  const handleBack = useCallback(() => {
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    
    // Clear cached preprocessed data if going back from Step 1 (career path + profile)
    // This ensures fresh AI processing when returning
    if (currentStep === 1 && formData.preprocessedData) {
      console.log('🔄 Going back from Career Path step - clearing cached preprocessed data for fresh re-analysis');
      setFormData(prev => ({
        ...prev,
        preprocessedData: null,
        resumeData: null // Also clear resumeData to ensure fresh parse
      }));
    }
  }, [currentStep, formData.preprocessedData]);

  // Check if current step is valid
  // Step 0 (resume) requires explicit validation - must be true, not just "not false"
  const isStepValid = useMemo(() => {
    if (currentStep === 0) {
      return stepValidation[0] === true;
    }
    return stepValidation[currentStep] !== false;
  }, [stepValidation, currentStep]);

  // Render current step component
  const StepComponent = steps[currentStep];

  return (
    <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', py: 4 }}>
      <Paper sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#1F2833', border: '1px solid rgba(0,255,245,0.2)' }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Level Up Your Career
          </Typography>
          <Typography variant="body2" sx={{ color: '#00FFF5', fontWeight: 600, mb: 1 }}>
            Your Roadmap to Promotion & Higher Pay
          </Typography>
          <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
            Get data-driven insights, gap analysis, and a personalized action plan
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
              Progress
            </Typography>
            <Typography variant="caption" sx={{ color: '#00FFF5', fontWeight: 600 }}>
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#0a0a0a',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                borderRadius: 4
              }
            }}
          />
          {autoSaving && (
            <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={12} sx={{ color: '#00FFF5' }} />
              Saving...
            </Typography>
          )}
        </Box>

        {/* Stepper */}
        <Stepper 
          activeStep={currentStep} 
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ mb: 4 }}
        >
          {stepConfig.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: index <= currentStep ? '#00FFF5' : '#262626',
                      color: index <= currentStep ? '#0B0C10' : '#737373',
                      fontWeight: 700,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {index < currentStep ? '✓' : step.icon}
                  </Box>
                )}
                sx={{
                  '& .MuiStepLabel-label': {
                    color: index <= currentStep ? '#fafafa' : '#737373',
                    fontWeight: index === currentStep ? 600 : 400
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: index === currentStep ? 600 : 400 }}>
                  {step.label}
                </Typography>
                {!isMobile && (
                  <Typography variant="caption" sx={{ color: '#737373' }}>
                    {step.description}
                  </Typography>
                )}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ mb: 4, minHeight: 300 }}>
          {currentStep === 3 ? (
            // Market Verdict Card has different props
            <StepComponent
              formData={formData}
              onNext={handleNext}
              onBack={handleBack}
            />
          ) : (
            <StepComponent
              formData={formData}
              updateFormData={updateFormData}
              onValidationChange={(isValid) => handleStepValidation(currentStep, isValid)}
              user={user}
              isAuthenticated={isAuthenticated}
              onRequireAuth={onRequireAuth}
            />
          )}
        </Box>

        {/* Navigation Buttons - hidden on Market Verdict step (it has its own) */}
        {currentStep !== 3 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={currentStep === 0}
              sx={{
                color: '#a1a1aa',
                '&:disabled': {
                  color: '#525252'
                }
              }}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid || autoSaving}
              sx={{
                background: isStepValid ? 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)' : '#262626',
                color: isStepValid ? '#0B0C10' : '#737373',
                fontWeight: 700,
                px: 4,
                '&:hover': {
                  background: isStepValid ? 'linear-gradient(90deg, #00CCC4 0%, #7D00CC 100%)' : '#262626',
                },
                '&:disabled': {
                  background: '#262626',
                  color: '#525252'
                }
              }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CareerPathWizard;

